// Typed wrappers for the /v1/merchants/:id/reports/* surface.
//
// All three endpoints stream a binary download (CSV / JSON / XLSX) so we
// pull the response as a Blob and let the caller pipe it to saveBlob().
// The bearer + X-Merchant-ID injection happens automatically through the
// apiClient axios interceptor; responseType:'blob' bypasses the
// bigint-aware transformResponse on the way back.
import { apiClient } from "./client";

export type ReportName = "statement" | "audit" | "reconciliation";
export type ReportFormat = "csv" | "json" | "xlsx";

export interface DownloadReportArgs {
  merchantId: number;
  report: ReportName;
  /** Inclusive lower bound. YYYY-MM-DD; the panel uses native HTML5 date inputs. */
  from: string;
  /** Exclusive upper bound. */
  to: string;
  format: ReportFormat;
}

/**
 * downloadReport — fetch the chosen report+window+format as a Blob.
 *
 * Throws on HTTP error (4xx/5xx); the caller wraps in
 * apiErrorMessage to surface the backend's structured `code` (e.g.
 * window_too_large, unsupported_format).
 *
 * The 5xx-side timeout: the backend caps each export at 5 minutes
 * (handlers.streamingContext). The axios default is 0 (no client
 * timeout), so this won't be the limiting factor.
 */
export async function downloadReport({
  merchantId,
  report,
  from,
  to,
  format,
}: DownloadReportArgs): Promise<Blob> {
  const resp = await apiClient.get<Blob>(
    `/v1/merchants/${merchantId}/reports/${report}`,
    {
      params: { from, to, format },
      responseType: "blob",
    },
  );
  return resp.data;
}
